import {
  ArrowUpRight,
  Clock3,
  FileText,
  Heart,
  House,
  Info,
  Layers,
  LayoutGrid,
  LockKeyhole,
  Mail,
  MapPin,
  MessageCircle,
  Newspaper,
  Phone,
  ShieldCheck,
  ShoppingCart,
  Sparkles,
  Tags,
  Truck,
} from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';

import { useTranslation } from '@sshop/i18n';
import { Container } from '@sshop/ui';

import { SocialLinks, useContactList } from '@/features/contact';
import { useLocale } from '@/shared/hooks/useLocale';
import { useCmsNavigation } from '@/shared/hooks/useCmsNavigation';
import { routes } from '@/shared/lib/routes';
import { PaymentCardMarks } from '@/shared/ui/PaymentCardMarks';

import { Brand } from './Header/Brand/Brand';

const titleClass = 'text-fg-primary mb-4 text-sm font-semibold';
const linkClass =
  'text-fg-secondary hover:text-accent focus-visible:ring-accent inline-flex min-h-11 min-w-0 items-center gap-2.5 rounded-md transition-colors duration-fast ease-token focus-visible:outline-none focus-visible:ring-2';
const tileClass =
  'border-border-subtle bg-bg-subtle/70 hover:border-accent hover:text-accent focus-visible:ring-accent text-fg-secondary flex h-full min-h-14 min-w-0 items-center gap-2.5 rounded-xl border p-3 text-xs font-medium leading-snug transition-colors focus-visible:outline-none focus-visible:ring-2';
const tileLabelClass = 'min-w-0 flex-1 [overflow-wrap:anywhere]';
const browseLinks = [
  { key: 'home', Icon: House, to: routes.home },
  { key: 'catalog', Icon: LayoutGrid, to: routes.catalog },
  { key: 'cart', Icon: ShoppingCart, to: routes.cart },
  { key: 'promotions', Icon: Tags, to: routes.promotions },
  { key: 'favorites', Icon: Heart, to: routes.favorites },
] as const;
const infoLinks = [
  { key: 'about', Icon: Info, to: routes.about },
  { key: 'news', Icon: Newspaper, to: routes.news },
  { key: 'contact', Icon: MessageCircle, to: routes.contact },
] as const;
const trustLinks = [
  { key: 'shipping', Icon: Truck, to: routes.shippingPayment },
  { key: 'privacy', Icon: LockKeyhole, to: routes.privacyPolicy },
  { key: 'guarantee', Icon: ShieldCheck, to: routes.guarantee },
  { key: 'terms', Icon: FileText, to: routes.termsConditions },
] as const;

export function Footer() {
  const { t } = useTranslation();
  const locale = useLocale();
  const { data: cmsNavigation } = useCmsNavigation();
  const footerPages = cmsNavigation?.filter((page) => page.footer) ?? [];
  const collectionPages = footerPages.filter((page) => page.key === 'collections');
  const informationPages = footerPages.filter((page) => page.key !== 'collections');
  const { pathname } = useLocation();
  const currentPath = pathname.replace(/\/+$/, '');
  const followsNewsSection = currentPath === `/${locale}` || currentPath === `/${locale}/news`;
  const { data: contacts } = useContactList();
  const contact = contacts?.[0];
  const mapQuery = [contact?.address, contact?.city, contact?.country].filter(Boolean).join(', ');

  return (
    <footer
      className={`border-border-subtle border-t ${followsNewsSection ? 'bg-bg-subtle' : 'bg-bg-subtle/70 mt-12'}`}
    >
      <Container size="xl" className="py-8 sm:py-10 lg:py-14">
        <div className="border-border-subtle bg-bg-canvas grid overflow-hidden rounded-2xl border shadow-sm lg:grid-cols-[minmax(0,1fr)_minmax(0,1.65fr)] lg:rounded-3xl">
          <section className="border-border-subtle relative overflow-hidden border-b p-6 sm:p-8 lg:border-r lg:border-b-0 lg:p-10 xl:row-span-2">
            <div
              className="bg-accent/10 pointer-events-none absolute -top-20 -right-20 h-56 w-56 rounded-full blur-3xl"
              aria-hidden
            />
            <Link
              to={routes.home(locale)}
              className="focus-visible:ring-accent relative inline-flex rounded-lg outline-none focus-visible:ring-2"
              aria-label={t('app.homeAria')}
            >
              <Brand />
            </Link>
            <p className="text-fg-secondary relative mt-5 max-w-sm text-sm leading-6 sm:text-base">
              {t('footer.tagline')}
            </p>
            <Link
              to={routes.special(locale)}
              className="bg-accent text-accent-fg duration-base ease-token relative mt-6 inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold shadow-sm transition-transform hover:-translate-y-0.5 hover:shadow-md"
            >
              <Sparkles className="h-4 w-4" aria-hidden />
              <span>{t('footer.shopDeals')}</span>
              <ArrowUpRight className="h-4 w-4" aria-hidden />
            </Link>

            <div className="min-[420px]:grid-cols-2 relative mt-8 grid grid-cols-1 gap-2 sm:max-w-md">
              {trustLinks.map(({ key, Icon, to }) => (
                <Link
                  key={key}
                  to={to(locale)}
                  className="border-border-subtle bg-bg-subtle/70 hover:border-accent focus-visible:ring-accent min-h-14 flex min-w-0 items-center gap-2.5 rounded-xl border p-3 transition-colors focus-visible:outline-none focus-visible:ring-2"
                >
                  <Icon className="text-accent h-5 w-5 shrink-0" aria-hidden />
                  <span className="text-fg-secondary min-w-0 flex-1 text-xs font-medium leading-snug [overflow-wrap:anywhere]">
                    {t(`home.trust.${key}.title`)}
                  </span>
                </Link>
              ))}
            </div>
          </section>

          <div className="grid content-start gap-8 p-6 sm:grid-cols-2 sm:p-8 lg:p-10 xl:pb-6">
            <nav className="min-w-0" aria-label={t('footer.browse')}>
              <h2 className={titleClass}>{t('footer.browse')}</h2>
              <ul className="grid grid-cols-2 gap-2">
                {browseLinks.map(({ key, Icon, to }) => (
                  <li key={key} className="min-w-0">
                    <Link to={to(locale)} className={tileClass}>
                      <Icon className="text-accent h-5 w-5 shrink-0" aria-hidden />
                      <span className={tileLabelClass}>{t(`nav.${key}`)}</span>
                    </Link>
                  </li>
                ))}
                {collectionPages.map((page) => (
                  <li key={page.key} className="min-w-0">
                    <Link to={page.url} className={tileClass}>
                      <Layers className="text-accent h-5 w-5 shrink-0" aria-hidden />
                      <span className={tileLabelClass}>{page.title}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            </nav>

            <nav className="min-w-0" aria-label={t('footer.info')}>
              <h2 className={titleClass}>{t('footer.info')}</h2>
              <ul className="grid grid-cols-2 gap-2">
                {infoLinks.map(({ key, Icon, to }) => (
                  <li key={key} className="min-w-0">
                    <Link to={to(locale)} className={tileClass}>
                      <Icon className="text-accent h-5 w-5 shrink-0" aria-hidden />
                      <span className={tileLabelClass}>{t(`nav.${key}`)}</span>
                    </Link>
                  </li>
                ))}
                {informationPages.map((page) => (
                  <li key={page.key} className="min-w-0">
                    <Link to={page.url} className={tileClass}>
                      <FileText className="text-accent h-5 w-5 shrink-0" aria-hidden />
                      <span className={tileLabelClass}>{page.title}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            </nav>
          </div>

          <section
            className="border-border-subtle bg-bg-subtle/40 border-t p-6 sm:p-8 lg:col-span-2 xl:col-span-1 xl:col-start-2 xl:row-start-2 xl:mx-10 xl:bg-transparent xl:px-0 xl:pt-4 xl:pb-8"
            aria-label={t('footer.contact')}
          >
            <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
              <h2 className="text-fg-primary text-sm font-semibold">{t('footer.contact')}</h2>
              {contact ? (
                <SocialLinks facebook={contact.facebook} instagram={contact.instagram} />
              ) : null}
            </div>
            <div className="grid gap-x-6 gap-y-2 text-sm sm:grid-cols-2">
              {contact?.phone ? (
                <a href={`tel:${contact.phone}`} className={`${linkClass} w-full`}>
                  <Phone className="text-accent h-4 w-4 shrink-0" aria-hidden />
                  <span className="break-all">{contact.phone}</span>
                </a>
              ) : null}
              {contact?.email ? (
                <a href={`mailto:${contact.email}`} className={`${linkClass} w-full`}>
                  <Mail className="text-accent h-4 w-4 shrink-0" aria-hidden />
                  <span className="break-all">{contact.email}</span>
                </a>
              ) : null}
              {mapQuery ? (
                <a
                  href={`https://maps.google.com/?q=${encodeURIComponent(mapQuery)}`}
                  target="_blank"
                  rel="noreferrer"
                  className={`${linkClass} ${contact?.workingHours ? '' : 'xl:col-span-2'}`}
                >
                  <MapPin className="text-accent mt-0.5 h-4 w-4 shrink-0" aria-hidden />
                  <span>{mapQuery}</span>
                </a>
              ) : null}
              {contact?.workingHours ? (
                <p className="text-fg-secondary min-h-11 flex items-center gap-2.5">
                  <Clock3 className="text-accent mt-0.5 h-4 w-4 shrink-0" aria-hidden />
                  <span>{contact.workingHours}</span>
                </p>
              ) : null}
              {!contact ? <p className="text-fg-muted">{t('footer.contactHint')}</p> : null}
            </div>
          </section>
        </div>
      </Container>

      <div className="border-border-subtle border-t">
        <Container
          size="xl"
          className="text-fg-secondary min-h-14 flex flex-col items-center justify-between gap-3 py-3 text-center text-xs sm:flex-row sm:text-left"
        >
          <span>
            &copy; {new Date().getFullYear()} SSHOP. {t('footer.rights')}
          </span>
          <PaymentCardMarks className="order-first sm:order-none" />
        </Container>
      </div>
    </footer>
  );
}
